/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import colors from "qqq/assets/theme/base/colors";
import React, {useState} from "react";


///////////////////////////////////////////////////////////////////////////////////
// put this sx on the element whose hover should reveal the icon (e.g., the page //
// title).  Keyboard focus on the icon reveals it too, so it can be tabbed to.   //
// The icon appears right away, but waits half a second before hiding once the   //
// mouse leaves - so it doesn't vanish while the user is moving toward it.       //
///////////////////////////////////////////////////////////////////////////////////
export const REVEAL_HOVER_COPY_LINK_ICON_SX = {
   "&:hover .hoverCopyLinkIcon, & .hoverCopyLinkIcon:focus-visible": {
      opacity: 1,
      maxWidth: "2rem",
      transform: "none",
      transitionDelay: "0s",
   }
};


interface HoverCopyLinkIconProps
{
   id?: string;
   tooltip: string;

   //////////////////////////////////////////////////////////////////////////////
   // either a link to copy (and the icon says "Copied" itself), or a callback //
   // that does the copying and its own reporting.                             //
   //////////////////////////////////////////////////////////////////////////////
   linkToCopy?: () => string;
   onClick?: () => void;
}


/*******************************************************************************
 ** a link icon that slides out beside a title while the title is hovered, and
 ** copies a link when clicked.
 *******************************************************************************/
export default function HoverCopyLinkIcon({id, tooltip, linkToCopy, onClick}: HoverCopyLinkIconProps): JSX.Element
{
   const [copyResult, setCopyResult] = useState(null as "copied" | "failed" | null);

   /*******************************************************************************
    **
    *******************************************************************************/
   const handleClick = async () =>
   {
      if (onClick)
      {
         onClick();
         return;
      }

      try
      {
         await navigator.clipboard.writeText(linkToCopy());
         setCopyResult("copied");
      }
      catch (e)
      {
         console.log("Error copying link to clipboard", e);
         setCopyResult("failed");
      }
      setTimeout(() => setCopyResult(null), 2000);
   };

   const title = copyResult == "copied" ? "Copied" : copyResult == "failed" ? "Couldn't copy the link" : tooltip;
   return (
      <Tooltip title={title} placement="top">
         <IconButton
            id={id}
            className="hoverCopyLinkIcon"
            onClick={handleClick}
            aria-label={tooltip}
            sx={{
               p: 0,
               ml: "0.25rem",
               mb: 1,
               fontSize: "0.75rem",
               color: colors.secondary.main,
               fontVariationSettings: "'wght' 100",
               opacity: 0,
               maxWidth: 0,
               overflow: "hidden",
               transform: "translateX(-0.5rem)",
               transition: "opacity 150ms ease-out, max-width 150ms ease-out, transform 150ms ease-out",
               transitionDelay: "0.5s",
            }}
         >
            <Icon fontSize="small">link</Icon>
         </IconButton>
      </Tooltip>
   );
}
